import type { MasterFieldDef } from './masters/types';
import { foodImageFields } from './foodMasterImageSpecs';

/** Coerce API/DB metadataFields into a safe array (null, objects, etc.). */
export function normalizeMetadataFields(
  fields: MasterFieldDef[] | null | undefined | unknown,
): MasterFieldDef[] {
  if (!Array.isArray(fields)) return [];
  return fields.filter(
    (field): field is MasterFieldDef =>
      !!field
      && typeof field === 'object'
      && typeof field.key === 'string'
      && field.key.length > 0,
  );
}

const SKIP_TEXT_TO_MASTER = new Set([
  'iso3Code', 'phoneCode', 'isoNumeric', 'swiftCode', 'iataCode', 'postalCodePattern',
  'referral_code', 'fieldKey', 'gateway', 'ifscPrefix', 'foodCode', 'sku', 'skuCode', 'serviceCode',
  'locale', 'ianaId', 'operatorSlug', 'restaurantSlug', 'icon', 'imageUrl', 'address',
  'phone', 'stationType', 'targetScreen', 'unit', 'startTime', 'endTime', 'start', 'end',
  'description', 'attributeCombination', 'hex', 'symbol', 'decimalSeparator', 'thousandSeparator',
  'utcOffset', 'abbreviation', 'nativeName', 'invoicePrefix', 'nationality', 'flagUrl',
  'capacity',
]);

export const MASTER_REF_BY_FIELD_KEY: Record<string, string> = {
  parentCategoryCode: 'food_categories',
  attributeCode: 'food_attributes',
  addonGroupCode: 'addon_groups',
  vehicleTypeCode: 'vehicle_types',
  defaultCurrency: 'currencies',
  secondaryCurrency: 'currencies',
  defaultLanguage: 'languages',
  timezone: 'timezones',
};

export const MASTER_REF_BY_MASTER_TYPE: Partial<Record<string, Record<string, string>>> = {
  food_attribute_values: { attributeCode: 'food_attributes' },
  ride_attribute_values: { attributeCode: 'ride_attributes' },
  food_sub_categories: { parentCategoryCode: 'food_categories' },
  grocery_sub_categories: { parentCategoryCode: 'grocery_categories' },
  fleet_branches: {},
  ride_images: { serviceCode: 'trip_types' },
  ride_categories: { serviceTypes: 'trip_types' },
  vehicle_types: { serviceTypes: 'trip_types' },
};

export const LEGACY_PARENT_META_KEY: Partial<Record<string, string>> = {
  food_sub_categories: 'parentCategoryCode',
  grocery_sub_categories: 'parentCategoryCode',
  food_attribute_values: 'attributeCode',
  ride_attribute_values: 'attributeCode',
};

const FOOD_MASTER_IMAGE_TYPES = new Set([
  'restaurant_types',
  'food_categories',
  'food_sub_categories',
  'cuisine_types',
  'addon_groups',
  'food_addons',
  'dietary_types',
  'food_images',
]);

function isImageMasterField(field: Pick<MasterFieldDef, 'key' | 'type'>): boolean {
  return field.type === 'image' || field.key === 'imageUrl' || field.key === 'icon';
}

const FOOD_IMAGE_FIELD_BY_MASTER: Partial<Record<string, () => MasterFieldDef>> = {
  restaurant_types: () => foodImageFields.restaurantType(),
  food_categories: () => foodImageFields.category(),
  food_sub_categories: () => foodImageFields.category(),
  cuisine_types: () => foodImageFields.cuisine(),
  addon_groups: () => foodImageFields.addon(),
  food_addons: () => foodImageFields.addon(),
  dietary_types: () => foodImageFields.dietaryIcon(),
  food_images: () => foodImageFields.thumbnail(),
};

function stripImageDimensionConstraints(field: MasterFieldDef): MasterFieldDef {
  const {
    imageMinWidth: _minW,
    imageMinHeight: _minH,
    imageMaxWidth: _maxW,
    imageMaxHeight: _maxH,
    imageAspectRatio: _ratio,
    ...rest
  } = field;
  return rest;
}

/** Inject food image fields when API schema omits them; no enforced dimension limits. */
export function enrichFoodImageMetadataFields(
  fields: MasterFieldDef[] | null | undefined = [],
  masterTypeKey: string,
): MasterFieldDef[] {
  const safeFields = normalizeMetadataFields(fields);
  if (!FOOD_MASTER_IMAGE_TYPES.has(masterTypeKey)) return safeFields;
  let working = safeFields;
  if (!working.some(isImageMasterField)) {
    const factory = FOOD_IMAGE_FIELD_BY_MASTER[masterTypeKey];
    if (factory) {
      working = [factory(), ...working];
    }
  }
  return working.map((field) => {
    if (!isImageMasterField(field)) return field;
    const preserveDietaryLimits = masterTypeKey === 'dietary_types' && field.key === 'icon';
    const preserveFoodImageLimits = masterTypeKey === 'food_images';
    const cleaned = preserveDietaryLimits
      ? {
        ...field,
        imageMaxWidth: field.imageMaxWidth ?? 256,
        imageMaxHeight: field.imageMaxHeight ?? 256,
        imageAspectRatio: field.imageAspectRatio ?? '1:1',
      }
      : preserveFoodImageLimits
        ? {
          ...field,
          imageMaxWidth: field.imageMaxWidth ?? 1200,
          imageMaxHeight: field.imageMaxHeight ?? 800,
        }
        : stripImageDimensionConstraints(field);
    return {
      ...cleaned,
      type: cleaned.type === 'text' ? 'image' as const : cleaned.type,
      helperText: cleaned.helperText && !/Recommended \d+×\d+px/.test(cleaned.helperText)
        ? cleaned.helperText
        : preserveDietaryLimits
          ? 'PNG, JPEG, or WebP · max 256×256px'
          : preserveFoodImageLimits
            ? 'PNG, JPEG, or WebP · max 1200×800px'
            : 'PNG, JPEG, or WebP',
    };
  });
}

export function resolveMasterRefType(field: MasterFieldDef, masterTypeKey: string): string | null {
  if (field.type === 'zone' || field.key === 'zoneCode') return null;
  if (field.type === 'master' && field.masterType) return field.masterType;
  if (SKIP_TEXT_TO_MASTER.has(field.key)) return null;

  const contextual = MASTER_REF_BY_MASTER_TYPE[masterTypeKey]?.[field.key];
  if (contextual) return contextual;

  if (MASTER_REF_BY_FIELD_KEY[field.key]) return MASTER_REF_BY_FIELD_KEY[field.key];

  return null;
}

export function enrichMetadataFields(fields: MasterFieldDef[] | null | undefined = [], masterTypeKey: string): MasterFieldDef[] {
  const safeFields = normalizeMetadataFields(fields);
  return safeFields.map((field) => {
    const masterType = resolveMasterRefType(field, masterTypeKey);
    if (!masterType || field.type === 'master' || field.type === 'zone') return field;
    if (['boolean', 'number', 'select', 'currency', 'percent', 'color', 'json', 'master', 'entity', 'time', 'image'].includes(field.type)) {
      return field;
    }
    const label = String(field.label || field.key || '');
    return {
      ...field,
      type: 'master' as const,
      masterType,
      label: label.replace(/\s+Code$/i, '').replace(/\s+Slug$/i, '') || label,
      helperText: field.helperText || `Select from ${masterType.replace(/_/g, ' ')} list`,
    };
  });
}

export function getMasterTypeForField(field: MasterFieldDef, masterTypeKey: string): string | null {
  if (field.type === 'master' && field.masterType) return field.masterType;
  return resolveMasterRefType(field, masterTypeKey);
}

/** Match child master row to selected parent code (parentId or legacy metadata) */
export function matchesParentMaster(
  child: { parentId?: string | { _id?: string }; metadata?: Record<string, unknown> },
  parentCode: string,
  parentRow?: { _id: string },
  legacyMetaKey?: string,
): boolean {
  if (!parentCode) return true;
  const childParentId = typeof child.parentId === 'object'
    ? child.parentId?._id
    : child.parentId;
  if (parentRow?._id && childParentId) return String(childParentId) === parentRow._id;
  if (legacyMetaKey && child.metadata?.[legacyMetaKey] === parentCode) return true;
  return false;
}
